/***
 *
 * Product Slider
 *
 * This is essentially the stock Shopify slider with auto-resize / mouse drag support / thumbnail orientation options
 * The core functions of this class are named / behave identically to the stock Shopify slider so that it can be slotted in place
 * Don't rename functions or change their behaviour if you don't know how to validate vs. the original
 *
 */

if (!customElements.get('product-slider')) {
    customElements.define(
        'product-slider',

        class ProductSlider extends HTMLElement {
            constructor() {
                super();
            }

            connectedCallback() {
                const tasks = [
                    this.setElements.bind(this),
                    this.setMediaSubscription.bind(this),
                    this.setDragConfiguration.bind(this),
                    this.setIntersectionObserver.bind(this),
                    this.setDragBindings.bind(this),
                    this.setGlobalBindings.bind(this),
                    this.onLoaded.bind(this)
                ];
                this.yieldTasks(tasks);
            }

            disconnectedCallback() {
                if (this.mediaQueryChangeUnsubscriber) {
                    this.mediaQueryChangeUnsubscriber();
                }
                this.unsetDragBindings();
                this.mutationObserver?.disconnect();
                this.resizeObserver?.disconnect();
                this.prevButton?.removeEventListener('click', this.onButtonClickHandler);
                this.nextButton?.removeEventListener('click', this.onButtonClickHandler);
                this.sliderContainer?.removeEventListener('scroll', this.onScrollHandler);
                this.sliderContainer?.removeEventListener('scrollend', this.onScrollEndHandler);
                this.sliderContainer?.removeEventListener(
                    'mousemove',
                    this.disableHoverZoomHandler
                );
            }

            yieldToMain() {
                return new Promise((resolve) => {
                    setTimeout(resolve, 0);
                });
            }

            async yieldTasks(tasks) {
                if (!tasks?.length) return;
                while (tasks.length > 0) {
                    const task = tasks.shift();
                    task();
                    await this.yieldToMain();
                }
            }

            onLoaded() {
                this.classList.add('product-slider--loaded');
                this.isLoaded = true;
                this.setPages();
            }

            onButtonClick(event) {
                event.preventDefault();
                const step = event.target.getAttribute('data-step') || 1;
                const scrollOffset = this.isSideView
                    ? this.sliderContainer.scrollTop
                    : this.sliderContainer.scrollLeft;
                this.slideScrollPosition =
                    event.currentTarget.name === 'next'
                        ? scrollOffset + step * this.sliderItemOffset
                        : scrollOffset - step * this.sliderItemOffset;
                this.setSlidePosition(this.slideScrollPosition);
            }

            onMediaQueryChange(event) {
                if (!event || !event.currentMedia) return;
                this.currentMediaQuery = event.currentMedia;
                if (
                    this.isThumbnail &&
                    this.currentMediaQuery.type !== p80.mediaQueryService.mediaTypes.large
                ) {
                    this.previousIsSideView =
                        this.gallery.getAttribute('data-thumbnail-position') === 'side'
                            ? true
                            : false;
                    this.isSideView = false;
                } else if (this.isThumbnail) {
                    this.isSideView = this.previousIsSideView;
                }

                this.snapSlide = null;
                this.setIntersectionObserver();

                this.touchSupport = event.touchSupport;
                if (this.touchSupport) {
                    this.unsetDragBindings();
                    this.classList.add('product-slider--scroll-snap');
                } else if (!this.touchSupport && !this.dragBound) {
                    this.setDragBindings();
                    this.classList.remove('product-slider--scroll-snap');
                }
            }

            setElements() {
                this.sliderContainer = this.querySelector('[id^="Slider-"]');
                this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
                this.currentPageElement = this.querySelector('.product-slider__counter--current');
                this.pageTotalElement = this.querySelector('.product-slider__counter--total');
                this.prevButton = this.querySelector('button[name="previous"]');
                this.nextButton = this.querySelector('button[name="next"]');
                this.gallery = this.closest('media-gallery');

                this.isThumbnail = this.classList.contains('product-thumbnail-slider')
                    ? true
                    : false;
                this.isSideView =
                    this.isThumbnail &&
                    this.gallery.getAttribute('data-thumbnail-position') === 'side'
                        ? true
                        : false;
                this.previousIsSideView = this.isThumbnail ? this.isSideView : null;
                this.isQuickAdd = this.closest('quick-add-modal') ? true : false;
            }

            setMediaSubscription() {
                this.currentMediaQuery = p80.mediaQueryService.currentMediaQuery;
                this.touchSupport = p80.mediaQueryService.touchSupport;
                if (
                    this.touchSupport &&
                    this.currentMediaQuery.type === p80.mediaQueryService.mediaTypes.small
                ) {
                    this.classList.add('product-slider--scroll-snap');
                }
                if (this.currentMediaQuery.type !== p80.mediaQueryService.mediaTypes.large) {
                    this.isSideView = false;
                }
            }

            setDragConfiguration() {
                if (!this.sliderContainer) return;

                this.dragEnabled = false;
                this.pageOffset = this.isSideView
                    ? this.getBoundingClientRect().top + window.scrollY
                    : this.getBoundingClientRect().left + window.scrollX;
                this.eventStartOffset = 0;
                this.eventStartScrollOffset = 0;
                this.velocity = 0;
                this.velocityCoefficient = this.isThumbnail ? 1.2 : 1.5;
                this.velocityDegradation = this.isThumbnail ? 0.9 : 0.95;
                this.dragDegradation = 0.65;
            }

            unsetDragBindings() {
                this.dragBound = false;
                this.sliderContainer?.removeEventListener('mousedown', this.enableDragHandler);
                this.sliderContainer?.removeEventListener('mouseup', this.disableDragHandler);
                this.sliderContainer?.removeEventListener('mouseleave', this.disableDragHandler);
                this.sliderContainer?.removeEventListener('mousemove', this.onDragHandler);
            }

            setDragBindings() {
                if (
                    !this.sliderContainer ||
                    (this.touchSupport &&
                        this.currentMediaQuery.type === p80.mediaQueryService.mediaTypes.small)
                )
                    return;
                this.dragBound = true;

                this.enableDragHandler = this.enableDrag.bind(this);
                this.disableDragHandler = this.disableDrag.bind(this);
                this.onDragHandler = this.onDrag.bind(this);
                this.velocityHandler = this.setVelocity.bind(this);
                this.sliderContainer?.addEventListener('mousedown', this.enableDragHandler, false);
                this.sliderContainer?.addEventListener('mouseup', this.disableDragHandler, false);
                this.sliderContainer?.addEventListener(
                    'mouseleave',
                    this.disableDragHandler,
                    false
                );
                this.sliderContainer?.addEventListener('mousemove', this.onDragHandler, false);
            }

            setGlobalBindings() {
                if (!this.sliderContainer) return;
                this.onScrollHandler = this.onScroll.bind(this);
                this.onScrollEndHandler = this.onScrollEnd.bind(this);
                this.onResizeHandler = this.onResize.bind(this);
                this.disableHoverZoomHandler = this.disableHoverZoom.bind(this);
                this.onChangeHandler = this.onSlidePrepend.bind(this);
                this.onButtonClickHandler = this.onButtonClick.bind(this);

                this.sliderContainer?.addEventListener('scroll', this.onScrollHandler, {
                    passive: true
                });
                this.sliderContainer?.addEventListener('scrollend', this.onScrollEndHandler);
                this.prevButton?.addEventListener('click', this.onButtonClickHandler);
                this.nextButton?.addEventListener('click', this.onButtonClickHandler);

                if (!this.isThumbnail)
                    this.sliderContainer.addEventListener(
                        'mousemove',
                        this.disableHoverZoomHandler
                    );

                this.resizeObserver = new ResizeObserver((entries) =>
                    this.onResizeHandler(entries)
                );
                this.resizeObserver.observe(this.sliderContainer);
                this.mediaQueryChangeUnsubscriber = subscribe(
                    PUB_SUB_EVENTS.mediaQueryChange,
                    (event) => {
                        this.onMediaQueryChange(event);
                    }
                );
                this.mutationObserver = new MutationObserver(this.onChangeHandler);
                this.mutationObserver.observe(this.sliderContainer, {
                    childList: true
                });
            }

            setIntersectionObserver() {
                if (this.observer) this.observer.disconnect();
                const options = {
                    root: this.sliderContainer,
                    threshold: 0.6
                };

                if (this.isThumbnail) {
                    const negativeMargin =
                        this.currentMediaQuery.type === p80.mediaQueryService.mediaTypes.large
                            ? '-80%'
                            : '-70%';
                    options.rootMargin = this.isSideView
                        ? `10% 0px ${negativeMargin} 0px`
                        : !this.isSideView && this.previousIsSideView
                          ? `0px ${negativeMargin} 0px 10%`
                          : `0px ${negativeMargin} 0px 10%`;
                } else {
                    options.rootMargin = `0px 10% 0px 0px`;
                }

                this.observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            this.snapSlide = entry.target;
                        }
                    });
                }, options);

                this.sliderItems.forEach((slide) => {
                    this.observer.observe(slide);
                });
            }

            setSlidePosition(position) {
                const left = !this.isSideView || !this.isThumbnail ? position : 0;
                const top = this.isThumbnail && this.isSideView ? position : 0;
                this.sliderContainer.scrollTo({
                    top,
                    left,
                    behavior: 'smooth'
                });
            }

            setPages() {
                if (!this.isLoaded) return;

                if (this.sliderItems.length < 2) {
                    this.sliderItemOffset = 0;
                    this.setHeight();
                    return;
                }

                if (this.isThumbnail) {
                    this.sliderItemOffset = this.isSideView
                        ? this.sliderItems[1].offsetTop - this.sliderItems[0].offsetTop
                        : this.sliderItems[1].offsetLeft - this.sliderItems[0].offsetLeft;
                    this.slidesPerPage = this.isSideView
                        ? Math.floor(
                              (this.sliderContainer.clientHeight - this.sliderItems[0].offsetTop) /
                                  this.sliderItemOffset
                          )
                        : Math.floor(
                              (this.sliderContainer.clientWidth - this.sliderItems[0].offsetLeft) /
                                  this.sliderItemOffset
                          );
                } else {
                    this.sliderItemOffset =
                        this.sliderItems[1].offsetLeft - this.sliderItems[0].offsetLeft;
                    this.slidesPerPage = Math.floor(
                        (this.sliderContainer.clientWidth - this.sliderItems[0].offsetLeft) /
                            this.sliderItemOffset
                    );
                }

                this.totalPages = this.sliderItems.length - this.slidesPerPage + 1;
                this.update();
            }

            resetPages() {
                this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
                this.setPages();
            }

            onScroll() {
                this.isScrolling = true;
                this.update();
            }

            onScrollEnd() {
                this.isScrolling = false;
                const tasks = [this.setHeight.bind(this)];
                if (!this.isThumbnail) tasks.unshift(this.setAttributes.bind(this));
                this.yieldTasks(tasks);
            }

            onResize() {
                const tasks = [
                    this.setPages.bind(this),
                    this.onResizeStart.bind(this),
                    this.setHeight.bind(this),
                    this.onResizeEnd.bind(this)
                ];
                this.yieldTasks(tasks);
            }

            onResizeStart() {
                this.classList.add('product-slider--no-animate');
                this.classList.add('product-slider--resizing');
            }

            onResizeEnd() {
                clearTimeout(this.resizeTimer);
                this.resizeTimer = setTimeout(() => {
                    this.classList.remove('product-slider--no-animate');
                    this.classList.remove('product-slider--resizing');
                }, 300);
            }

            onSlidePrepend(mutation) {
                const tasks = [
                    this.resetPages.bind(this),
                    this.setSlidePosition.bind(this, 0),
                    this.setHeight.bind(this)
                ];
                this.yieldTasks(tasks);
            }

            setAttributes() {
                // Since Shopify completely rearranges slides on variant change (but not thumbnails) this is probably the simplest way to do this unless we construct a map
                // Using the current page index won't work since the indexes change on variant change between sliders and diverge
                let activeMediaId = null;
                this.sliderItems?.forEach((slide, index) => {
                    if (index === this.currentPage - 1) {
                        slide.classList.add('is-active');
                        activeMediaId = slide.getAttribute('data-media-id');
                    } else {
                        slide.classList.remove('is-active');
                    }
                });
                if (!activeMediaId) return;
                const thumbnailSlider = this.parentElement.querySelector(
                    '.product-thumbnail-slider'
                );
                if (!thumbnailSlider) return;
                const thumbnails = thumbnailSlider.querySelectorAll(
                    '.product-thumbnail-slider__thumbnails > li'
                );
                thumbnails?.forEach((thumbnail, index) => {
                    const id = thumbnail.getAttribute('data-target');
                    const button = thumbnail.querySelector('button');
                    if (id === activeMediaId) {
                        button?.setAttribute('aria-current', true);
                        thumbnailSlider.isSlideVisible(thumbnail);
                    } else {
                        button?.removeAttribute('aria-current');
                    }
                });
            }

            setHeight() {
                const currentSlide =
                    this.sliderContainer.querySelector('.is-active') ||
                    this.sliderItems[this.currentPage - 1];

                if (!currentSlide) return;
                const activeMedia =
                    currentSlide.querySelector('.product-media-container') ||
                    currentSlide.querySelector('model-opener') ||
                    currentSlide.querySelector('product-model') ||
                    currentSlide.querySelector('.product__media');
                if (!activeMedia) return;

                if (this.style.getPropertyValue('--height') === `${activeMedia.scrollHeight}px`) {
                    return;
                }
                this.style.setProperty('--height', `${activeMedia.scrollHeight}px`);
            }

            // This function can't be renamed as Shopify calls this externally
            update() {
                const scrollOffset =
                    this.isThumbnail && this.isSideView
                        ? this.sliderContainer.scrollTop
                        : this.sliderContainer.scrollLeft;

                const previousPage = this.currentPage;
                this.currentPage = Math.round(scrollOffset / this.sliderItemOffset) + 1;

                if (this.currentPageElement && this.pageTotalElement) {
                    this.currentPageElement.textContent = this.currentPage;
                    this.pageTotalElement.textContent = this.totalPages;
                }

                if (this.isSlideVisible(this.sliderItems[0], 0, true) && scrollOffset === 0) {
                    this.prevButton?.setAttribute('disabled', 'disabled');
                } else {
                    this.prevButton?.removeAttribute('disabled');
                }
                if (this.isSlideVisible(this.sliderItems[this.sliderItems.length - 1], 10, true)) {
                    this.nextButton?.setAttribute('disabled', 'disabled');
                } else {
                    this.nextButton?.removeAttribute('disabled');
                }
            }

            isSlideVisible(element, offset = 0, self) {
                const sliderBounding = this.sliderContainer.getBoundingClientRect();
                const elementBounding = element.getBoundingClientRect();

                const sliderSize =
                    this.isThumbnail && this.isSideView
                        ? Math.floor(sliderBounding.height)
                        : Math.floor(sliderBounding.width);
                const sliderOffset =
                    this.isThumbnail && this.isSideView
                        ? this.sliderContainer.scrollTop
                        : this.sliderContainer.scrollLeft;
                const elementSize =
                    this.isThumbnail && this.isSideView
                        ? Math.floor(elementBounding.height)
                        : Math.floor(elementBounding.width);
                const elementOffset =
                    this.isThumbnail && this.isSideView ? element.offsetTop : element.offsetLeft;

                const lastVisibleSlide = sliderSize + sliderOffset - offset;
                const isSlideVisible =
                    elementOffset + elementSize <= lastVisibleSlide &&
                    elementOffset >= sliderOffset;

                if (isSlideVisible) {
                    return true;
                }
                // This function is called by Shopify functions in media-gallery.js and global.js
                // We always return true to those functions as they don't mesh well with the slider additions made here
                // Otherwise it bypasses our scroll/updating and creates some less than stellar jank
                // It also lets the main carousel auto-scroll the child thumbnails by querying this same function
                else if (!isSlideVisible && !self) {
                    if (!this.isScrolling) this.setSlidePosition(elementOffset);
                    return true;
                }
                return false;
            }

            disableHoverZoom() {
                clearTimeout(this.disableHoverZoomTimeout);
                this.classList.add('product-slider--no-zoom');
                this.disableHoverZoomTimeout = setTimeout(() => {
                    this.classList.remove('product-slider--no-zoom');
                }, 500);
            }

            /* Velocity and event managers for mouse drag */

            enableDrag(event) {
                // This disables drag events when 3d models are active
                if (!this.isThumbnail) {
                    const mediaModel = event?.target.closest('.media-type-model');
                    const modelViewer = mediaModel?.querySelector('model-viewer');
                    if (
                        modelViewer &&
                        !modelViewer.classList.contains('shopify-model-viewer-ui__disabled')
                    )
                        return;
                }
                this.dragEnabled = true;
                this.pageOffset =
                    this.isThumbnail && this.isSideView
                        ? this.getBoundingClientRect().top + window.scrollY
                        : this.getBoundingClientRect().left + window.scrollX;
                this.eventStartOffset =
                    this.isThumbnail && this.isSideView
                        ? event.pageY - this.pageOffset
                        : event.pageX - this.pageOffset;
                this.eventStartScrollOffset =
                    this.isThumbnail && this.isSideView
                        ? this.sliderContainer.scrollTop
                        : this.sliderContainer.scrollLeft;
                cancelAnimationFrame(this.animationID);
            }

            disableDrag(event) {
                if (!this.dragEnabled) return;
                this.dragEnabled = false;
                cancelAnimationFrame(this.animationID);

                if (this.velocity != 0)
                    this.animationID = requestAnimationFrame(this.velocityHandler);
            }

            onDrag(event) {
                if (!this.dragEnabled) return;
                event.preventDefault();
                const eventOffset =
                    this.isThumbnail && this.isSideView
                        ? event.pageY - this.pageOffset
                        : event.pageX - this.pageOffset;
                const previousOffset =
                    this.isThumbnail && this.isSideView
                        ? this.sliderContainer.scrollTop
                        : this.sliderContainer.scrollLeft;
                const dragDistance = eventOffset - this.eventStartOffset;
                const offset = this.eventStartScrollOffset - dragDistance * this.dragDegradation;

                if (this.isThumbnail && this.isSideView) {
                    this.sliderContainer.scrollTop = Math.floor(offset);
                } else {
                    this.sliderContainer.scrollLeft = Math.floor(offset);
                }
                this.velocity =
                    this.isThumbnail && this.isSideView
                        ? this.sliderContainer.scrollTop - previousOffset
                        : this.sliderContainer.scrollLeft - previousOffset;
            }

            setVelocity() {
                const scrollOffset =
                    this.isThumbnail && this.isSideView
                        ? this.sliderContainer.scrollTop
                        : this.sliderContainer.scrollLeft;
                const scrollHeight =
                    this.isThumbnail && this.isSideView
                        ? this.sliderContainer.scrollHeight
                        : this.sliderContainer.scrollWidth;
                const globalOffset =
                    this.isThumbnail && this.isSideView
                        ? this.sliderContainer.offsetHeight
                        : window.scrollX;

                if (scrollOffset === 0 || scrollOffset === scrollHeight - globalOffset) {
                    this.velocity = 0;
                    return;
                }

                if (Math.abs(this.velocity) <= 0.5) {
                    if (!this.snapSlide) return;
                    this.velocity = 0;
                    const snapOffset =
                        this.isThumbnail && this.isSideView
                            ? this.snapSlide.offsetTop
                            : this.snapSlide.offsetLeft;
                    this.setSlidePosition(snapOffset);
                    return;
                }
                if (this.isThumbnail && this.isSideView) {
                    this.sliderContainer.scrollTop += this.velocity * this.velocityCoefficient;
                } else {
                    this.sliderContainer.scrollLeft += this.velocity * this.velocityCoefficient;
                }
                this.velocity = this.velocity * this.velocityDegradation;
                this.animationID = requestAnimationFrame(this.velocityHandler);
            }
        }
    );
}
